import '../index.scss';

import clsx from 'clsx';
import { delay } from 'lodash-es';
import { CSSProperties, PropsWithChildren, ReactElement, useContext, useEffect, useRef } from 'react';

import { LuiModalAsyncInstanceContext } from './LuiModalAsyncInstanceContext';

export interface LuiModalAsyncProps {
  className?: string;
  style?: CSSProperties;
  /**
   * Close on clicking overlay
   */
  closeOnOverlayClick?: boolean;
}

/**
 * This creates the dialog element.
 */
export const LuiModalAsync = ({
  style,
  className,
  closeOnOverlayClick,
  children,
}: PropsWithChildren<LuiModalAsyncProps>): ReactElement => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const { close } = useContext(LuiModalAsyncInstanceContext);

  // The only way to create a modal dialog is to call showModal, open attribute will not work
  useEffect(() => {
    // Check if it's open already to support .vite hot deploys
    if (!dialogRef.current?.open) {
      dialogRef.current?.showModal?.();
    }
  }, []);

  useEffect(() => {
    // Dialogs auto select the first focusable element, this is in case you don't want that
    // For example we don't want to select the help button by default, so we add data-noautofocus
    delay(() => {
      const d = dialogRef.current;
      if (!d) {
        return;
      }
      const input = (d.querySelectorAll('[data-autofocus]')[0] ??
        d.querySelectorAll('[type=file]')[0] ??
        d.querySelectorAll('button:not([data-noautofocus]),input:not([data-noautofocus])')[0]) as
        | HTMLElement
        | undefined;

      if (input) {
        input.focus?.();
        if ('select' in input) {
          (input as HTMLInputElement).select?.();
        }
      }
    }, 100);
  }, []);

  // You cannot override dialogs position from absolute.
  return className?.includes('storybook') ? (
    <div className={clsx('LuiModalAsync', className)} style={style}>
      {children}
    </div>
  ) : (
    <dialog
      role={'dialog'}
      className={clsx('LuiModalAsync', className)}
      style={style}
      ref={dialogRef}
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
      onClick={(e) => closeOnOverlayClick && e.target === e.currentTarget && close()}
    >
      {children}
    </dialog>
  );
};
